import { defineStore, storeToRefs } from "pinia"
import { Role } from "@/models/role/role.model"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store.ts"
import { computed } from "vue"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { RoleRepository } from "@/repositories/role-repository"
import { type KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { getSubclassForRoleType } from "@/helper/role-helper"
import { RoleServiceProvider } from "@/models/role/role-service-provider.model"
import { RolePlanner } from "@/models/role/role-planner.model"
import { ModelFactory } from "@/factories/model-factory"
import { RoleFinancialOfficer } from "@/models/role/role-financial-officer.model"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"

export const useRoleStore = defineStore("roleStore", () => {
  const knownBusinessEntitiesContactsStore = useKnownBusinessEntitiesContactsStore()
  const { knownBusinessEntitiesContacts } = storeToRefs(knownBusinessEntitiesContactsStore)

  const roles = computed(() => {
    const _roles: Role[] = []
    for (const knownBusinessEntityContact of knownBusinessEntitiesContacts.value) {
      if (knownBusinessEntityContact.roles) {
        _roles.push(...knownBusinessEntityContact.roles)
      }
    }
    return _roles
  })

  const rolesServiceProvider = computed(() => {
    return roles.value.filter((role) => role instanceof RoleServiceProvider)
  })

  const { pushToRelatedObjects, removeFromRelatedObjects } = useRelatedObjectsManager(
    knownBusinessEntitiesContacts,
    (role: Role) => role.knownBusinessEntityContact,
    (knownBusinessEntityContact: KnownBusinessEntityContact) => knownBusinessEntityContact.id!,
    (knownBusinessEntityContact: KnownBusinessEntityContact) => knownBusinessEntityContact.roles
  )

  /* Apply socket synchronisation handlers for each Role-inherited class */
  const concreteRoleTypes = [Role, RolePlanner, RoleServiceProvider, RoleFinancialOfficer]
  for (const roleType of concreteRoleTypes) {
    useSocketSynchronizationHandlers(
      roleType as ModelConstructorSignature<any, any>,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }

  async function saveRole<T extends Role>(
    role: T,
    knownBusinessEntityContact: KnownBusinessEntityContact
  ) {
    const result = await new RoleRepository().create(role, knownBusinessEntityContact.id)
    const Subclass = getSubclassForRoleType(role.type)
    const instance = ModelFactory.createModel(
      Subclass as ModelConstructorSignature<any, any>,
      result.data
    )

    knownBusinessEntityContact.roles.push(instance)
  }

  async function updateRole(role: Role) {
    const modelCopy = { ...role }
    if (!modelCopy.id) {
      throw new Error("Cannot update model without id.")
    }

    return await new RoleRepository().update(
      modelCopy.knownBusinessEntityContact,
      modelCopy.id,
      modelCopy
    )
  }

  async function deleteRole(role: Role) {
    if (!role.id) {
      throw new Error("Cannot delete model without id.")
    }

    await new RoleRepository().delete(role.knownBusinessEntityContact, role.id)
    removeFromRelatedObjects(role)
  }

  return {
    roles,
    rolesServiceProvider,
    saveRole,
    updateRole,
    deleteRole
  }
})
